import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  PARTNER_COLORS,
  PARTNER_NEUTRAL_HEX,
  partnerColorHex,
  type PartnerColor,
} from "../../constants/partnerColors";

/** A partner holding a colour, as `GET /partners/colors` lists it. */
export interface PartnerColorUsage {
  _id: string;
  companyName: string;
  color: PartnerColor;
}

interface PartnerColorPickerProps {
  /**
   * The colour shown. `undefined` means not decided yet — a new partner before
   * the server has said which colour it would get — and is drawn as "Automatic".
   */
  value: PartnerColor | null | undefined;
  onChange: (color: PartnerColor | null) => void;
  /** Colours other partners already hold, for the "already used" ring. */
  usage?: PartnerColorUsage[];
  /** This partner's own id, so its own colour is not reported as taken. */
  partnerId?: string;
  busy?: boolean;
}

/**
 * The swatch button in a partner's header, and the palette it opens.
 *
 * A fixed palette rather than a free colour wheel: twelve shades that stay
 * distinguishable from each other and from the status colours, which a wheel
 * would let anyone undo with one drag. A colour another partner already holds
 * is still offered — two clients sharing a stripe is allowed — but ringed, so
 * it is a choice and not an accident.
 */
export function PartnerColorPicker({
  value,
  onChange,
  usage = [],
  partnerId,
  busy = false,
}: PartnerColorPickerProps) {
  const { t } = useTranslation("partners");
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Who else holds each colour — this partner excluded.
  const usedBy = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const entry of usage) {
      if (entry._id === partnerId) continue;
      map.set(entry.color, [...(map.get(entry.color) ?? []), entry.companyName]);
    }
    return map;
  }, [usage, partnerId]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (color: PartnerColor | null) => {
    setOpen(false);
    if (color !== value) onChange(color);
  };

  const label =
    value === undefined
      ? t("colors.auto")
      : value === null
        ? t("colors.none")
        : t(`colors.${value}`);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={t("colors.label")}
        className="flex items-center gap-2 rounded-lg border border-[rgba(11,15,14,0.12)] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-ink transition-colors hover:border-[rgba(11,15,14,0.24)] disabled:opacity-60"
      >
        <span
          className={`h-4 w-4 shrink-0 rounded-[5px] ${
            value === undefined ? "border border-dashed border-[#A9A69C]" : ""
          }`}
          style={{
            background: value === undefined ? "transparent" : partnerColorHex(value),
          }}
        />
        {label}
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-mist" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-mist" />
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t("colors.label")}
          className="absolute left-0 top-full z-30 mt-2 w-[268px] rounded-xl border border-[rgba(11,15,14,0.12)] bg-white p-3 shadow-xl"
        >
          <div className="mb-2.5 grid grid-cols-6 gap-2">
            {PARTNER_COLORS.map(({ key, hex }) => {
              const others = usedBy.get(key) ?? [];
              const selected = value === key;
              const name = t(`colors.${key}`);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => pick(key)}
                  aria-pressed={selected}
                  aria-label={name}
                  title={
                    others.length
                      ? t("colors.alsoUsedBy", {
                          color: name,
                          names: others.join(", "),
                        })
                      : name
                  }
                  className={`relative h-[34px] w-[34px] rounded-[9px] border-2 transition-transform hover:scale-105 ${
                    selected
                      ? "border-ink shadow-[inset_0_0_0_2px_#fff]"
                      : "border-transparent"
                  }`}
                  style={{ background: hex }}
                >
                  {others.length > 0 && (
                    <span className="absolute -right-[3px] -top-[3px] h-[11px] w-[11px] rounded-full border-2 border-ink bg-white" />
                  )}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => pick(null)}
            aria-pressed={value === null}
            className="flex w-full items-center gap-2 rounded-lg border border-dashed border-[#CFCBBE] bg-[#FAF9F5] px-2.5 py-1.5 text-[12px] font-semibold text-ink"
          >
            <span
              className="h-4 w-4 shrink-0 rounded-[5px]"
              style={{ background: PARTNER_NEUTRAL_HEX }}
            />
            {t("colors.noneOption")}
            {value === null && <Check className="ml-auto h-3.5 w-3.5" />}
          </button>

          <p className="mt-2 text-[11px] leading-snug text-mist">
            {t("colors.note")}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * The small dot beside a client's name — the same colour as the row's stripe,
 * for where a stripe has no edge to sit on. Renders nothing for a client that
 * is not a partner.
 */
export function PartnerColorDot({
  color,
  className = "",
}: {
  /** From `partnerStripe` — null means not a partner, so no dot. */
  color: string | null;
  className?: string;
}) {
  if (!color) return null;
  return (
    <span
      aria-hidden
      className={`inline-block h-[9px] w-[9px] shrink-0 rounded-full ${className}`}
      style={{ background: color }}
    />
  );
}
